import {
    collection,
    type CollectionReference,
    doc,
    type DocumentReference,
    getDocs,
    onSnapshot,
    type Query,
    type QueryDocumentSnapshot,
    type QuerySnapshot,
    updateDoc,
    writeBatch,
} from 'firebase/firestore';
import { observable } from 'mobx';

import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentStatus } from 'types/DocumentStatus';
import { DocumentType } from 'types/DocumentType';
import { arrayChunks, arrayInPlaceReject } from 'utils/Array';
import { deepCopyForFirestore } from 'utils/Copy';
import DisposerManager from 'utils/DisposerManager';

import DocumentStore from './DocumentStore';
import Store, { type BecomeObservableOptions } from './Store';

/**
 * A DocumentStore class as a collection constructs it: the collection is
 * always the parent, and docRef is absent only for the `childObject`
 * instance made in DocumentCollection's constructor.
 *
 * `parent` is `any` because subclasses declare their own collection as the
 * parent type (Ad takes an AdsManager, and so on), and one type can't say
 * "whichever collection owns this class".
 */
export type DocumentStoreClass<T> = new (
    parent: any,
    docRef?: DocumentReference
) => T;

/**
 * @class DocumentCollection
 * @description
 * The DocumentCollection class is used to manage a collection of `DocumentStore` instances. 
 * It manages the lifecycle of the child documents as documents on the Firestore database are updated, modified, creatived, and removed.
 * It also provides methods for querying and filtering the collection. 
 * 
 * @example
 * ```
 * let myCollection = new DocumentCollection(myParent, MyDocumentStoreClassReference);
 * ```
 * 
 * @param {DocumentStore | CollectionReference} parent - The parent of the collection. This can be either a `DocumentStore` or a `CollectionReference`. 
 * @param {DocumentStoreClass<ChildType>} childClass - The class of the child documents. This is used to create new instances of the child documents.
 * */
export default class DocumentCollection<
    ChildType extends DocumentStore<any> = any,
    ChildSchema = any,
> extends Store {
    protected readonly disposer = new DisposerManager();

    children: ChildType[] = observable.array([]);
    // collectionRef: CollectionReference;
    // collectionType: DocumentCollectionType;
    childType: DocumentType = DocumentType.DEFAULT;
    childClass: DocumentStoreClass<ChildType>;
    status = DocumentStatus.LOADING;
    parent: DocumentStore<any>;
    parentCollectionRef: CollectionReference;
    hasChildren = false;
    mounted = false;

    /**
     * The query method is used to get the query for the collection. This can be overridden to provide custom filtering and sorting.
     *
     * The function may also return null to indicate no query can be made. In this case the load will be skipped and
     * an empty document set will be assumed.
     *
     * @example
     * ```
     * query = () => {
     *  return this.collectionRef.where('archived', '==', true).orderBy('dateModified', 'desc');
     * }
     * // sets the query to return all archived documents, sorted by dateModified
     * ```
     *
     * @returns The query for the collection.
     * */
    query: () => Query | null = () => {
        return this.collectionRef;
    };

    batchSize: number = 400;
    childObject: ChildType | null = null;
    /**
     * If the child class has a `collectionType` property, it will be used as the collection type for the collection and return that value.
     * Otherwise, it will return the default collection type.
     * */
    get collectionType(): DocumentCollectionType {
        if (this.childObject) return this.childObject.collectionType;
        return DocumentCollectionType.DEFAULT;
    }
    
    /**
     * Returns the Firestore collection reference for the collection.
     * */
    get collectionRef() {
        const parent = this.parent;
        if (parent instanceof DocumentStore && this.parent.docRef) {
            return collection(this.parent.docRef, this.collectionType);
        } else {
            return this.parentCollectionRef;
        }
    }

    constructor(
        parent: DocumentStore<any, any> | CollectionReference,
        childClass: DocumentStoreClass<ChildType>
    ) {
        super();
        if (parent instanceof DocumentStore) this.parent = parent;
        else this.parentCollectionRef = parent;
        this.childObject = new childClass(this);
        this.childType = this.childObject.type;
        this.childClass = childClass;
        if (!this.collectionType)
            throw new Error(
                'DocumentCollection: Collection type not defined for child class: ' +
                    childClass.name
            );
        // TODO: Not required for now but may be in the future. Find a way to exclude state management on properties from the this base class.
        // this.becomeObservable({ except: ['_onCreate'] });
    }

    /**
     * Keeps `disposer` out of MobX. See the note on `neverObserve` in Store.ts
     * for why a base class has to do this by override.
     */
    becomeObservable(options?: BecomeObservableOptions) {
        super.becomeObservable({
            except: [...(options?.except ?? []), 'disposer'],
        });
    }

    get all() {
        return this.children;
    }

    /**
     * Returns all documents in the collection that are not archived.
     * */
    get allAvailable() {
        return this.all.filter(item => !item.data.archived);
    }
    
    get length() {
        return this.children.length;
    }
    
    /**
     * Returns all documents in the collection that exist locally only. These documents have not been saved to the database yet.
     * Could be used to save all local documents to the database.
     * */
    get allLocal() {
        return this.children.filter((item) => item.isLocalOnly);
    }

    get context() {
        return this.parent.context;
    }

    get loading() {
        return this.status === DocumentStatus.LOADING;
    }

    get loaded() {
        return this.status === DocumentStatus.LOADED;
    }

    /**
     * Returns a promise that resolves to a boolean indicating whether the collection exists in the database.
     * */
    exists = async () => {
        const collection = await getDocs(this.collectionRef);
        return collection.docs.length > 0;
    };

    /**
     * Loads the collection from the Firestore database.
     * */
    load = async () => {
        console.log(`DocumentCollection<${this.collectionType}>.load`);
        this.reset();
        // if(!this.collectionRef && this.parent) this.collectionRef = this.parent.docRef.collection(this.collectionType);

        this.status = DocumentStatus.LOADING;
        this.disposer.add(onSnapshot(this.collectionRef, this.onUpdate));

        this.children = await this.loadData();
        await this.onLoad();
        this.mounted = true;
        this.onReady();
        this.status = DocumentStatus.LOADED;
    };

    /**
     * This method is called after the collection has been loaded from the database. It is a place holder for child classes to override.
     * */
    onLoad = async () => {
        // This is a place holder for child classes to override.
    };

    /**
     * This method is called after the collection's onLoad method has resolved. It is a place holder for child classes to override.
     * */
    onReady = () => {};

    /**
     * This method is called after each child document is created. It is a place holder for child classes to override.
     * */
    onCreate = (_store: ChildType) => {};

    reload = async () => {
        await this.load();
    };

    _onCreate = async (
        docRef: DocumentReference,
        data: any
    ): Promise<ChildType> => {
        const childClass = this.childClass;
        const newChild = new childClass(this, docRef);
        data = { ...newChild.getDefaults(), ...data };
        newChild._update(data);
        newChild.onReady();
        return newChild;
    };

    loadData = async () => {
        const logPrefix = `DocumentCollection<${this.collectionType}>.load`;
        const loadQuery = this.query();

        if (loadQuery) {
            const response = await getDocs(loadQuery);
            console.log(
                `${logPrefix}: response.docs.length == ${response.docs.length}`
            );
            this.hasChildren = response.docs.length > 0;
            if (this.hasChildren) {
                const childrenData = response.docs;
                const children = [];

                for (const i in childrenData) {
                    const doc = childrenData[i];
                    const data = doc.data() as ChildSchema;
                    const docRef = doc.ref;
                    const newDoc = await this._onCreate(docRef, data);
                    newDoc.updatesEnabled = false;
                    children.push(newDoc);
                    this.onCreate(newDoc);
                }
                return children;
            }
        } else {
            console.log(`${logPrefix}: no query`);
            this.hasChildren = false;
        }
        return [];
    };

    /**
     * Adds a new document to the collection with the provided data.
     * @param data The data to be added to the new document.
     * @returns The newly created document.
     * */
    add = async (data: Partial<ChildSchema>): Promise<ChildType> => {
        const docRef: DocumentReference = doc(this.collectionRef);
        const newChild = await this._onCreate(docRef, data);
        newChild.status = DocumentStatus.LOADING;
        this.children.push(newChild);
        await Promise.all([
            // Bumps the parent's sort position; not worth failing the
            // creation over.
            this.parent?.updateDateModified().catch((e: unknown) => {
                console.error(
                    '[DocumentCollection.add] parent.updateDateModified failed',
                    e
                );
            }),
            newChild.save(),
        ]);

        return newChild;
    };

    /**
     * Adds a new document to the collection with the provided data. This document will not be synced with the database but will exist locally.
     * @param data The data to be added to the new document.
     * @returns The newly created document.
     * */
    addLocal = async (data: ChildSchema) => {
        console.log(`DocumentCollection<${this.collectionType}>.addLocal`);
        // TODO: might be possible to add these to a firebase batch, until they are to be synced. then commit the batch.
        const stub = await this._onCreate(doc(this.collectionRef), data);
        stub.isLocalOnly = true;
        console.log('IS LOCAL ONLY: ' + stub.isLocalOnly);
        this.children.push(stub);
        return stub;
    };

    /**
     * Creates a new document in the collection with the provided data. This document is not added as a child and simply returns a new document.
     * @param data The data to be added to the new document.
     * @returns The newly created document.
     * */
    create = async (data: ChildSchema) => {
        console.log(`DocumentCollection<${this.collectionType}>.create`);
        // TODO: might be possible to add these to a firebase batch, until they are to be synced. then commit the batch.

        // ???: This is copy-pasted (note the repeated comment above), but
        // it's not clear why `data` gets deep copied in this method and not
        // in the other one. I've maintained the pattern but one may need to
        // be made like the other.

        const stub = await this._onCreate(
            doc(this.collectionRef),
            deepCopyForFirestore(data)
        );
        stub.isLocalOnly = true;
        return stub;
    };

    /**
     * Removes multiple documents from the collection by an array of references to child documents.
     * @param set An array of document ids to be removed from the collection.
     * */
    removeMultiple = async (set: ChildType[]) => {
        const chunks = arrayChunks(set, this.batchSize);

        for (const chunk of chunks) {
            const batch = writeBatch(this.collectionRef.firestore);
            for (const child of chunk) {
                batch.delete(child.docRef);
            }
            await batch.commit();
        }
        await this.parent?.updateDateModified();
    };

    /**
     * Commits all local documents to the database.
     * */
    commitLocal = async () => {
        await this.commitDocuments(this.allLocal);
    }

    /**
     * Gets a document from the collection by its id.
     * @param id The id of the document to be retrieved.
     * @returns The document with the provided id.
     * */
    getById = (id: string) => {
        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            if (child.id === id) return child;
        }
    }

    /**
     * Gets a document from the collection by its name.
     * @param name The name of the document to be retrieved.
     * @returns The document with the provided name.
     * */
    getByName = (name: string) => {
        for (const item of this.all) {
            if (item.data.name === name) {
                return item;
            }
        }
    }

    /**
     * Removes a document from the collection by its id. This change will be synced with the database.
     * @param id The id of the document to be removed.
     * */
    remove = async (id: string) => {
        const child = this.getById(id);
        if (!child) return;

        child.reset();
        await child.delete();
        this.parent.updateDateModified();
        return id;
    };

    /**
     * Saves all documents with changes to the database in batches to avoid exceeding the Firestore write limit.
     * */
    saveAll = async () => { // Save all documents with changes.
        await this.commitDocuments(this.children);
    }

    private async commitDocuments(documents: ReadonlyArray<ChildType>) {
        for (const docBatch of arrayChunks(documents, this.batchSize)) {
            const batch = writeBatch(this.collectionRef.firestore);
            for (const doc of docBatch) {
                batch.set(doc.docRef, doc.data);
            }
            await batch.commit();
            for (const doc of docBatch) {
                doc.isLocalOnly = false;
            }
        }
    }

    private async setArchivedById(id: string, isArchived: boolean) {
        const targetDoc = this.getById(id);
        if (targetDoc?.data) {
            targetDoc.data.archived = isArchived;
        }
        const subjectDoc = doc(this.collectionRef, id);
        await updateDoc(subjectDoc, { archived: isArchived });
    }

    /**
     * Archives a document by its id. This sets the document property `archive` to true. 
     * @param id The id of the document to be archived.
     * */
    archive = (id: string) => {
        return this.setArchivedById(id, true);
    }

    /**
     * Unarchives a document by its id. This sets the document property `archive` to false.
     * @param id The id of the document to be unarchived.
     * */
    unarchive = (id: string) => {
        return this.setArchivedById(id, false);
    };

    /**
     * This is a private method meant is called when any document in the collection is updated, added, or removed.
     * @param snapshot The snapshot of the collection.
     * */
    onUpdate = async (snapshot: QuerySnapshot) => {
        console.log(
            `DocumentCollection<${this.collectionType}>.onUpdate: this.loading == ${this.loading}`
        );
        if (this.loading) return;
        const changed = snapshot.docChanges();
        for (const change of changed) {
            const doc: QueryDocumentSnapshot = change.doc;
            if (change.doc.metadata.hasPendingWrites) return; // !!! firestore will attempt to update data locally instantly. but some things don't work right with this.
            switch (change.type) {
                case 'added':
                    this.onAdded(doc);
                    break;
                case 'modified':
                    this.onModified(doc);
                    break;
                case 'removed':
                    this.onRemoved(doc);
                    break;
            }
        }
    };

    /**
     * This is a private method is called when a document is added to the collection.
     * It uses the provided child class reference to create a new instance of that class, hydrates it with the data from the document, and adds it to the collection as a child
     * @param doc The Firestore DocumentSnapshot that was added to the collection.
     * */
    private onAdded = async (doc: QueryDocumentSnapshot) => {
        console.log(
            `DocumentCollection<${this.collectionType}>.onAdded: doc.id == ${doc.id}`
        );
        const childExists = !!this.getById(doc.id);
        if (childExists) {
            this.onModified(doc);
            return;
        }

        const data = doc.data() as ChildSchema;
        const docRef = doc.ref;

        const newChild = await this._onCreate(docRef, data); // TODO: docmanager - figure this out
        newChild.onCreate(newChild.data);

        this.children.push(newChild);
    };

    /**
     * This is a private method is called when a document is removed from the collection.
     * */
    onRemoved = (removedDoc: QueryDocumentSnapshot) => {
        const removed: ChildType[] = [];
        arrayInPlaceReject(this.children, (scanDoc) => {
            if (scanDoc.id !== removedDoc.id) return false;
            removed.push(scanDoc);
            return true;
        });
        // Only once `children` no longer holds it, so nothing reading the
        // collection meanwhile sees a reset document.
        for (const scanDoc of removed) scanDoc.reset();
    };

    /**
     * This is a private method is called when a document is modified in the collection.
     * @param doc The Firestore DocumentSnapshot that was modified in the collection.
     * */
    onModified = (doc: QueryDocumentSnapshot) => {
        const data = doc.data() as ChildSchema;
        const docRef = doc.ref;
        const document = this.getById(docRef.id);
        // item.onUpdate(data);
        // document?.updateValidate(docPropeties); // TODO: docManager - updateValidate doesn't exist yet. maybe create it in the future.
        // check if data, which is an object, has changed
        if (document) {
            const isChanged =
                JSON.stringify(document.data) !== JSON.stringify(data);
            if (isChanged) {
                document?._update(data);
            }
        }
    };

    /**
     * This method resets the collection to its initial state. It clears all children, disposes of all listeners, and resets all properties.
     * */
    reset() {
        this.disposer.clear();
        this.children = [];
        // this.collectionType = DocumentCollectionType.NONE;
        this.childType = DocumentType.NONE;
        this.status = DocumentStatus.NONE;
        this.mounted = false;
    }
}
